import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { MAX_COMPARED_LAPS } from "./TraceWorkspace";
import { formatLapRef, type LapRef } from "../lib/api";
import { formatDelta, formatLapTime } from "../lib/format";
import type { LeaderboardEntry } from "../lib/types";

interface Group {
  key: string;
  trackName: string;
  carClass: string | null;
  entries: LeaderboardEntry[];
}

interface Props {
  entries: LeaderboardEntry[];
  /** Where a comparison of the ticked laps lives, e.g. `/compare`. */
  comparePath: string;
  /** Highlights the viewer's own rows, so they can find themselves. */
  currentUserId?: string;
}

/**
 * A leaderboard, grouped by track and class, that laps can be picked off.
 *
 * Grouped because a lap time only means something against another lap of the same
 * track in a car of the same class. Pickable because this is the one place in the
 * app that already knows which two drivers' laps are worth putting side by side —
 * whether the board is a team's or everybody's.
 */
export function LeaderboardTable({ entries, comparePath, currentUserId }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<LapRef[]>([]);
  const [group, setGroup] = useState<string | null>(null);

  const groups = useMemo<Group[]>(() => {
    const byKey = new Map<string, Group>();
    for (const entry of entries) {
      const key = `${entry.track_name}\u0000${entry.car_class ?? ""}`;
      const existing = byKey.get(key);
      if (existing) {
        existing.entries.push(entry);
      } else {
        byKey.set(key, {
          key,
          trackName: entry.track_name,
          carClass: entry.car_class,
          entries: [entry],
        });
      }
    }
    return [...byKey.values()];
  }, [entries]);

  function toggle(groupKey: string, entry: LeaderboardEntry) {
    const ref = { sessionId: entry.session_id, lap: entry.lap_number };
    // Laps overlay on distance around one track, so a selection cannot span two.
    // Picking a lap elsewhere starts again rather than refusing the click.
    const base = groupKey === group ? selected : [];
    const without = base.filter(
      (candidate) =>
        candidate.sessionId !== ref.sessionId || candidate.lap !== ref.lap,
    );

    setGroup(groupKey);
    setSelected(
      without.length < base.length
        ? without
        : base.length >= MAX_COMPARED_LAPS
          ? base
          : [...base, ref],
    );
  }

  const isSelected = (groupKey: string, entry: LeaderboardEntry) =>
    groupKey === group &&
    selected.some(
      (ref) => ref.sessionId === entry.session_id && ref.lap === entry.lap_number,
    );

  return (
    <div className="space-y-6">
      {groups.map((entry) => {
        const best = entry.entries[0]?.lap_time_ms ?? null;
        return (
          <div key={entry.key}>
            <div className="flex items-baseline gap-2">
              <h3 className="text-sm font-medium text-zinc-200">{entry.trackName}</h3>
              {entry.carClass && (
                <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-zinc-400">
                  {entry.carClass}
                </span>
              )}
            </div>

            <div className="mt-2 overflow-hidden rounded-xl border border-zinc-800">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-zinc-800">
                  {entry.entries.map((row, index) => {
                    const picked = isSelected(entry.key, row);
                    const mine = row.user_id === currentUserId;
                    return (
                      <tr
                        key={`${row.session_id}:${row.lap_number}`}
                        onClick={() => toggle(entry.key, row)}
                        title={t("leaderboard.pickHint")}
                        className={[
                          "cursor-pointer transition-colors",
                          picked
                            ? "bg-sky-500/10"
                            : mine
                              ? "bg-zinc-900/60 hover:bg-zinc-900"
                              : "hover:bg-zinc-900/40",
                        ].join(" ")}
                      >
                        <td className="w-10 px-3 py-2 text-right tabular-nums text-zinc-500">
                          {index + 1}
                        </td>
                        <td className="px-3 py-2">
                          <span className="flex items-center gap-2">
                            {row.avatar_url && (
                              <img
                                src={row.avatar_url}
                                alt=""
                                className="size-5 rounded-full ring-1 ring-zinc-700"
                              />
                            )}
                            <span className="text-zinc-200">{row.persona_name}</span>
                            {mine && (
                              <span className="text-xs text-zinc-500">
                                {t("leaderboard.you")}
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-zinc-500">{row.car_name ?? "—"}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-zinc-100">
                          {formatLapTime(row.lap_time_ms)}
                        </td>
                        <td className="w-20 px-3 py-2 text-right tabular-nums text-zinc-500">
                          {formatDelta(row.lap_time_ms, best)}
                        </td>
                        <td className="w-24 px-3 py-2 text-right">
                          {row.can_open_session && (
                            <Link
                              to={`/sessions/${row.session_id}`}
                              onClick={(event) => event.stopPropagation()}
                              className="text-xs text-zinc-500 hover:text-sky-400"
                            >
                              {t("leaderboard.session")} →
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {selected.length > 0 && (
        <div className="sticky bottom-4 flex items-center justify-between gap-4 rounded-xl border border-sky-500/40 bg-zinc-900/95 px-4 py-3 backdrop-blur">
          <span className="text-sm text-zinc-300">
            {t("leaderboard.selected", { count: selected.length })}
            {selected.length === 1 && (
              <span className="ml-2 text-zinc-500">
                {t("leaderboard.pickAnother")}
              </span>
            )}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSelected([])}
              className="rounded-md border border-zinc-700 px-2.5 py-1 text-xs text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
            >
              {t("leaderboard.clear")}
            </button>
            <button
              type="button"
              disabled={selected.length < 2}
              onClick={() =>
                navigate(`${comparePath}?laps=${selected.map(formatLapRef).join(",")}`)
              }
              className="rounded-lg bg-sky-500 px-3 py-1.5 text-sm font-medium text-zinc-950 transition-colors hover:bg-sky-400 disabled:opacity-40"
            >
              {t("leaderboard.compare")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
